import type { FC } from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import ApiIcon from '@mui/icons-material/Api';
import upi from 'src/components/logos/upi.png';
import imps from 'src/components/logos/imps.png';
import sepa from 'src/components/logos/sepa.png';
import fasterpayments from 'src/components/logos/fasterpayments.png';
import { PlatformWeBuilt } from 'src/components/home/platform-we-built';
import { DrivenByBestInClass } from 'src/components/home/driven-by-best-in-class';

export const HomeFeatures: FC = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 6, md: 8 }}
          alignItems="center"
          justifyContent="center"
        >
          {/* Code Sample Section */}
          <Grid xs={12}
            md={6}>
            <Box
              sx={{
                backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.800',
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                color: 'common.white',
                fontFamily: 'monospace',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                lineHeight: 1.6,
                overflow: 'auto',
                maxHeight: { xs: '300px', md: '400px' },
                boxShadow: '0 8px 32px 0 rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.1)',
                transform: { xs: 'none', md: 'perspective(1000px) rotateY(-5deg)' },
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: { xs: 'none', md: 'perspective(1000px) rotateY(0deg)' }
                }
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {`import requests

url = "https://swiftramp.com/api/v1/wallet/conversion/fiat/sell"

headers = {
    "X-Timestamp": "{{timestamp}}",
    "X-Client-Id": "{{client_id}}",
    "X-Request-Id": "{{request_id}}",
    "X-User-Id": "{{user_id}}",
    "X-Secret-Key": "{{secret_key}}"
}

data = {
    "source_id": "{{id}}",
    "fiat_symbol": "INR",
    "crypto_symbol": "USDT",
    "crypto_amount": 1.14
}

response = requests.post(url, headers=headers, json=data`}
              </pre>
            </Box>
          </Grid>

          {/* Text Content Section */}
          <Grid xs={12}
            md={6}>
            <Box id="products-section"
sx={{ maxWidth: '500px', mx: { xs: 'auto', md: 0 } }}>
              <Typography
                variant="h3"
                sx={{ mb: 3, fontWeight: 700 }}
              >
                A few lines of code
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 5, fontSize: '1.125rem', lineHeight: 1.6 }}
              >
                Bringing crypto to your business - Seamlessly integrate Web3 into your app with minimal code!
              </Typography>

              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, letterSpacing: '0.05em' }}
              >
                CHOOSE HOW YOU WANT TO INTEGRATE
              </Typography>

              <Stack spacing={3}
                sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    component="span"
                    sx={{
                      backgroundColor: 'primary.main',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  >
                    <CodeIcon sx={{ color: 'white' }} />
                  </Box>
                  <Typography variant="body1"
                    sx={{ fontWeight: 500 }}>Redirect to a web hosted SwiftRamp URL</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    component="span"
                    sx={{
                      backgroundColor: 'primary.main',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  >
                    <ApiIcon sx={{ color: 'white' }} />
                  </Box>
                  <Typography variant="body1"
                    sx={{ fontWeight: 500 }}>Use our best in class APIs</Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }}
                spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  href="https://cal.com/support-team-qsszsx"
                  target="_blank"
                  sx={{ py: 1.5, px: 3, borderRadius: 2, fontWeight: 600 }}
                >
                  Talk to us
                </Button>
                <Button
                  variant="outlined"
                  sx={{ py: 1.5, px: 3, borderRadius: 2, fontWeight: 600 }}
                >
                  Read our Docs
                </Button>
              </Stack>
            </Box>
          </Grid>




        </Grid>

        {/* Payment Methods Section */}
        <Box
          sx={{
            mt: { xs: 8, md: 12 },
            py: 6,
            backgroundColor: '#f5f5f7',
            borderRadius: 0,
            width: '100vw',
            position: 'relative',
            left: '50%',
            right: '50%',
            marginLeft: '-50vw',
            marginRight: '-50vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                mb: 4,
                fontWeight: 500,
                textAlign: 'center'
              }}
            >
              Supported Payment Methods
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 3, md: 5 },
                flexWrap: 'wrap'
              }}
            >
              <PaymentMethod icon={upi}
name="UPI" />
              <PaymentMethod icon={imps}
name="IMPS" />
              <PaymentMethod icon={sepa}
name="SEPA" />
              {/* <PaymentMethod icon={sepa} name="SEPA Instant" /> */}
              <PaymentMethod icon={fasterpayments}
name="Faster Payments" />
            </Box>
          </Container>
        </Box>

        {/* Add the Platform We Built section */}
        <PlatformWeBuilt />

        {/* Add the Driven by best in class section */}
        <DrivenByBestInClass />

      </Container>
    </Box>
  );
};

// Helper component for payment methods
const PaymentMethod: FC<{ icon: string; name: string }> = ({ icon, name }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      px: 0,
      py: 0
    }}
  >
    <Box
      component="img"
      src={icon}
      alt={name}
      sx={{
        width: 'auto',
        height: 30, // Adjust this value based on your PNG sizes
        objectFit: 'contain'
      }}
    />
    {/* <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: 'text.primary'
      }}
    >
      {name}
    </Typography> */}
  </Box>
);
